import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { resolveAvatar, type AvatarOptions, type DocumentItem } from '@document-explorer/core';

/**
 * One avatar cell: an image or initials, a name, or both.
 *
 * Everything about *what* to show is decided by `resolveAvatar()` in
 * `@document-explorer/core`, so this renders exactly what the React
 * `AvatarCell` renders — same initials, same colour, same tooltip.
 */
@Component({
  selector: 'de-avatar-cell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // `data-de-tooltip` drives a themed bubble that appears immediately; the name
  // also sits in screen-reader text, so nothing depends on hover.
  host: { class: 'de-avatar-cell', '[attr.data-de-tooltip]': 'avatar()?.title ?? null' },
  template: `
    @if (avatar(); as resolved) {
      @if (resolved.showAvatar) {
        <span
          class="de-avatar"
          [style.background]="showImage() ? 'transparent' : resolved.color"
          aria-hidden="true"
        >
          @if (showImage()) {
            <img [src]="resolved.imageUrl" alt="" loading="lazy" (error)="imageFailed.set(true)" />
          } @else {
            {{ resolved.initials }}
          }
        </span>
      }
      @if (resolved.showName) {
        <span class="de-avatar-label">{{ resolved.name }}</span>
      } @else if (resolved.name !== '') {
        <!-- With no visible label, the name still needs to reach assistive tech. -->
        <span class="de-sr-only">{{ resolved.name }}</span>
      }
    }
  `,
})
export class DeAvatarCellComponent {
  readonly item = input.required<DocumentItem>();
  /** The column key, used as the default name source. */
  readonly columnKey = input<string>('');
  readonly options = input<AvatarOptions>({});

  protected readonly imageFailed = signal(false);
  protected readonly avatar = computed(() => resolveAvatar(this.item(), this.columnKey(), this.options()));

  constructor() {
    // A new row in the same DOM position must not inherit the previous failure.
    effect(() => {
      this.avatar()?.imageUrl;
      this.imageFailed.set(false);
    });
  }

  protected readonly showImage = computed(() => {
    const resolved = this.avatar();
    return resolved?.imageUrl !== undefined && !this.imageFailed();
  });
}
